import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Check, ArrowRight, Eye, EyeOff } from "lucide-react";
import { setAuthToken } from "../lib/axiosAuth";
import { captureUtmsFromUrl, getStoredUtms } from "../lib/utmTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [form, setForm] = useState({ 
    name: "", 
    email: "", 
    password: "",
    acceptTerms: false 
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const validatePassword = (password) => {
    if (password.length < 8) return "La contraseña debe tener al menos 8 caracteres";
    if (!/[A-Z]/.test(password)) return "Debe incluir al menos una mayúscula";
    if (!/[0-9]/.test(password)) return "Debe incluir al menos un número";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[Register] Form submitted', { name: form.name, email: form.email });
    setError("");
    
    // Validate terms
    if (!form.acceptTerms) {
      const errorMsg = "Debes aceptar los términos y condiciones";
      console.error('[Register] Validation error:', errorMsg);
      setError(errorMsg);
      return;
    }
    
    // Validate password
    const passwordError = validatePassword(form.password);
    if (passwordError) {
      console.error('[Register] Password validation error:', passwordError);
      setError(passwordError);
      return;
    }
    
    console.log('[Register] Starting registration request...');
    setLoading(true);
    
    try {
      console.log('[Register] Sending POST to:', `${API}/auth/register`);

      // Re-capture in case the user landed directly on /register with
      // UTMs in the URL. Then pull the merged set from sessionStorage.
      captureUtmsFromUrl();
      const utms = getStoredUtms();

      const response = await axios.post(`${API}/auth/register`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        ...utms,
      }, { 
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        timeout: 30000, // 30 second timeout
      });
      
      console.log('[Register] Response received:', response.status, response.data);
      
      // Verify we have a valid user response
      if (!response.data || !response.data.user_id) {
        const errorMsg = 'Respuesta del servidor inválida - no se recibió user_id';
        console.error('[Register]', errorMsg, response.data);
        throw new Error(errorMsg);
      }
      
      // Safari ITP fallback: persist Bearer token alongside the cookie.
      if (response.data.session_token) setAuthToken(response.data.session_token);
      
      console.log('[Register] Success! User created:', response.data.user_id);
      console.log('[Register] Attempting navigation to /register/organization...');
      
      // Safari fix: Longer delay to ensure cookies are properly set and propagated
      // Safari requires more time than Chrome/Firefox for cookie propagation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('[Register] Cookie delay completed, navigating now...');
      
      // Redirect to organization setup
      navigate("/register/organization", { replace: true });
      
      console.log('[Register] Navigate called successfully');
    } catch (err) {
      console.error('[Register] Error caught:', {
        name: err.name,
        message: err.message,
        code: err.code,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // More specific error handling
      let errorMessage = "";
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.status === 400) {
        errorMessage = "Error en los datos enviados. Verifica tu información.";
      } else if (err.response?.status === 500) {
        errorMessage = "Error del servidor. Por favor intenta de nuevo.";
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = "La solicitud tardó demasiado. Verifica tu conexión.";
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = "No se pudo conectar con el servidor. Verifica tu conexión a internet.";
      } else if (err.message.includes('cookie') || err.message.includes('credentials')) {
        errorMessage = "Error de autenticación. Si usas Safari en modo privado, intenta en una ventana normal.";
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      } else {
        errorMessage = "Error desconocido al crear la cuenta. Por favor intenta de nuevo.";
      }
      
      console.error('[Register] Setting error message:', errorMessage);
      setError(errorMessage);
      
      // Force scroll to error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      console.log('[Register] Request completed, setting loading to false');
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/register/organization";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-cyan-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>
        
        <div className="relative z-10 max-w-lg">
          <img src="/notalia-logo.png" alt="Notalia" className="h-14 mb-10 brightness-0 invert" />
          
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            Cumplimiento AML/KYC
            <span className="text-teal-400"> simplificado</span>
          </h1>
          
          <p className="text-slate-300 text-lg mb-10 leading-relaxed">
            La plataforma líder para notarías, inmobiliarias y actividades vulnerables en México.
          </p>
          
          <div className="space-y-4">
            {[
              "Onboarding KYC automatizado",
              "Screening PEP/OFAC en tiempo real",
              "Reportes SAT y UIF listos para enviar",
              "Cumple con LFPIORPI desde el día uno"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-teal-400" />
                </div>
                <span className="text-slate-300">{feature}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-12 pt-8 border-t border-slate-700/50">
            <p className="text-slate-500 text-sm">
              Más de 500 organizaciones confían en Notalia
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/notalia-logo.png" alt="Notalia" className="h-10 mx-auto brightness-0 invert" />
          </div>
          
          {/* Progress indicator - 2 steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-white text-sm font-medium">1</div>
              <span className="text-teal-400 text-sm font-medium hidden sm:inline">Cuenta</span>
            </div>
            <div className="w-12 h-px bg-slate-700" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 text-sm">2</div>
              <span className="text-slate-500 text-sm hidden sm:inline">Organización</span>
            </div>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white" data-testid="register-heading">
              Crea tu cuenta
            </h2>
            <p className="text-slate-400 mt-2">
              Prueba gratuita de 14 días del plan Pro — sin tarjeta de crédito
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">Nombre completo</Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Juan Pérez García"
                required
                autoComplete="name"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 h-11"
                data-testid="register-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Correo electrónico</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="tu@empresa.com"
                required
                autoComplete="email"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 h-11"
                data-testid="register-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Mínimo 8 caracteres"
                  required
                  autoComplete="new-password"
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500 h-11 pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Mínimo 8 caracteres, una mayúscula y un número
              </p>
            </div>
            
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acceptTerms"
                name="acceptTerms"
                checked={form.acceptTerms}
                onChange={handleChange}
                className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-teal-500 focus:ring-teal-500 focus:ring-offset-slate-950"
              />
              <label htmlFor="acceptTerms" className="text-sm text-slate-400">
                Acepto los{" "}
                <a href="https://notalia.mx/terminos" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300">
                  Términos y Condiciones
                </a>
                {" "}y la{" "}
                <a href="https://notalia.mx/privacidad" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300">
                  Política de Privacidad
                </a>
              </label>
            </div>
            
            <Button
              type="submit"
              disabled={loading || !form.acceptTerms}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white font-medium h-12 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creando cuenta...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Crear cuenta
                  <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-slate-950 px-4 text-slate-500">o continúa con</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white h-12"
            data-testid="google-register-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continuar con Google
          </Button>

          <p className="text-center text-sm text-slate-500 mt-8">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-teal-400 hover:text-teal-300 font-medium" data-testid="login-link">
              Iniciar sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
